import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Grid, Typography, Paper, Button } from "@mui/material";
import AddDriverThemeProvider from "../../components/DriverDocuments/themeProvider";
import DocumentUploader from "../../components/DriverDocuments/UploadDocument";
import {
  GET_DRIVER_API,
  GET_DRIVER_DOCUMENTS,
  SUBMIT_DRIVER_DOCUMENTS,
} from "../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../reducers";
import UploadDriverAvatar from "../../components/DriverDocuments/UploadAvatar";
import { prepareDocuments } from "../../utils/documentUtils";
import { DocumentItemType } from "../../components/DriverDocuments/types";
import { useTranslation } from "react-i18next";
import { setDocumentsNotification } from "../../actions/valueDriverActions";
import Spinner from "../../components/Spinner";

const PHOTO_PROFILE_NAME = ["photo_profile", "PHOTO_PROFIL"];

const DriverDocuments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { driverId } = useParams();
  const profile = useSelector((state: AppState) => state.profile);
  const [driverDocuments, setDriverDocuments] = useState<DocumentItemType[]>(
    []
  );
  const [approvedAndPendingDocuments, setApprovedAndPendingDocuments] =
    useState<DocumentItemType[]>([]);
  const [driverData, setDriverData] = useState<{
    firstName?: string;
    lastName?: string;
  }>({});

  const [faildDocuments, setFaildDocuemnts] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchDocumentsLoading, setFetchDocumentsLoading] = useState(false);

  const isPhotoProfileMissing = !!driverDocuments.find((document) =>
    PHOTO_PROFILE_NAME.includes(document.name)
  )?.name;

  const isPhotoProfilePending = !!approvedAndPendingDocuments.find(
    (document) =>
      PHOTO_PROFILE_NAME.includes(document.name) &&
      document.status === "PENDING"
  )?.name;

  const form = useForm();

  const { handleSubmit } = form;

  const onSubmit = async (data) => {
    setLoading(true);
    const documents = Object.values(data).filter(
      (document) => document !== undefined
    );

    const result = await SUBMIT_DRIVER_DOCUMENTS(driverId, {
      documents,
    });

    if (result.data?.failedDocuments?.length) {
      const faildDocumentsObject = {};
      result.data?.failedDocuments.forEach((faildDoc) => {
        faildDocumentsObject[faildDoc.documentName] = faildDoc;
      });
      setFaildDocuemnts(faildDocumentsObject);
    } else {
      dispatch(
        setDocumentsNotification(
          t("fleet_mgt_driver's_documents_uploaded_notifiaction")
        )
      );
      navigate(`/chauffeurs`);
    }
    setLoading(false);
  };

  useEffect(() => {
    const getDriverDocs = async () => {
      setFetchDocumentsLoading(true);
      const documents = await GET_DRIVER_DOCUMENTS(driverId);
      // get the non approved or pending docs
      const approvedAndPendingDocuments = documents.data.documents.filter(
        (document) =>
          document.status === "APPROVED" || document.status === "PENDING"
      );

      const driverDocumentItemsConfig =
        profile?.profile?.config.MULTI_DRIVER_COMPANY?.DRIVER?.filter(
          (item) => item.type === "DOCUMENT"
        );
      const notUploadedDocuments = prepareDocuments(
        driverDocumentItemsConfig,
        approvedAndPendingDocuments
      );

      setDriverDocuments((notUploadedDocuments as DocumentItemType[]) || []);
      setApprovedAndPendingDocuments(approvedAndPendingDocuments);
      setFetchDocumentsLoading(false);
    };
    const getDriverData = async () => {
      setFetchDocumentsLoading(true);

      const driverData = await GET_DRIVER_API(driverId);
      setDriverData(driverData.data.driver);
      setFetchDocumentsLoading(false);
    };
    getDriverData();
    getDriverDocs();
  }, []);

  const watchedValues = form.watch();
  const isAnyFieldFilled = Object.values(watchedValues).some(
    (value) => value !== null && value !== undefined && value !== ""
  );

  return (
    <AddDriverThemeProvider>
      {fetchDocumentsLoading && <Spinner />}
      <Grid container spacing={2} sx={{ p: 3 }}>
        <Grid item xs={12} mb={8}>
          <Typography
            variant="h1"
            textAlign="start"
            fontWeight={700}
            fontSize={32}
          >
            {t("fleet_mgt_drivers_driver's_documents_title")}
          </Typography>
        </Grid>

        <Grid item>
          <Paper
            sx={{
              borderRadius: 2,
              p: 3,
            }}
          >
            <Grid container>
              <Typography variant="h4" fontWeight={600} textAlign="start">
                {t("fleet_mgt_driver_profile_upload_missing_documents_title")}{" "}
                {driverData?.firstName} {driverData?.lastName}
              </Typography>
              <Typography
                variant="subtitle1"
                fontSize={14}
                textAlign="start"
                mb={4}
              >
                {t(
                  "fleet_mgt_driver_profile_upload_missing_documents_description"
                )}
              </Typography>
            </Grid>
            <FormProvider {...form}>
              {(isPhotoProfileMissing || isPhotoProfilePending) && (
                <Grid container justifyContent="center" alignItems="center">
                  <Paper
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "#F1F1FE",
                      height: 168,
                      width: "100%",
                      borderRadius: 2,
                    }}
                  >
                    <UploadDriverAvatar
                      name={
                        driverDocuments.find((document) =>
                          PHOTO_PROFILE_NAME.includes(document.name)
                        )?.name
                      }
                      pendingProfilePhoto={approvedAndPendingDocuments.find(
                        (document) =>
                          PHOTO_PROFILE_NAME.includes(document.name) &&
                          document.status === "PENDING"
                      )}
                    />
                  </Paper>
                </Grid>
              )}

              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} mt={4}>
                  {driverDocuments
                    .filter(
                      (document) => !PHOTO_PROFILE_NAME.includes(document.name)
                    )
                    .map((field: DocumentItemType) => (
                      <Grid item xs={12} md={6}>
                        <DocumentUploader
                          field={field}
                          isFailed={
                            faildDocuments[field.name as unknown as string]
                          }
                          setFaildDocuemnts={setFaildDocuemnts}
                          dataTestId={`upload-driver-document-${field.name}`}
                        />
                      </Grid>
                    ))}

                  <Grid item xs={12} container gap={1}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      sx={{ flex: 1 }}
                      onClick={() => {
                        navigate(`/driver/${driverId}`);
                      }}
                      data-testid="skip-driver-documents"
                    >
                      {t("fleet_mgt_adding_driver's_documents_skip_cta")}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      data-testid="submit-driver-documents"
                      sx={{ flex: 1 }}
                      disabled={
                        !form.formState.isValid || !isAnyFieldFilled || loading
                      }
                    >
                      {t("fleet_mgt_adding_driver's_documents_submit_cta")}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </FormProvider>
          </Paper>
        </Grid>
      </Grid>
    </AddDriverThemeProvider>
  );
};

export default DriverDocuments;
